/* eslint-disable max-len */
import {
  ProductModel,
  CoverEnum
} from '@app/features/home/store/products/products.model';

import { CartDiscountTier, CartItem } from './cart.model';

export const MAX_QUANTITY = 32;
export const MAX_INSTALLMENTS = 8;
export const MIN_INSTALLMENT_VALUE = 30;

export type Promotion = {
  title: string;
  description: string;
  type: 'KIT_TIER' | 'KIT_EXTRA' | 'UNIT_TIER';
};

export function extractDiscountTiersFromProducts(
  products: ProductModel[]
): CartDiscountTier[] {
  const kitProducts = products.filter((product) => product.isKit);

  const discountTiers = kitProducts.map((kit) => {
    const percentage = parseFloat((kit.discountPercentage || 0).toFixed(4));
    const formattedPercentage = `${(percentage * 100).toFixed(1)}%`.replace(
      '.',
      ','
    );

    return {
      minQuantity: kit.quantity,
      percentage,
      coverType: kit.coverType,
      formattedPercentage,
      price: kit.unitPrice
    };
  });

  // Remove duplicates keeping the best price for each minQuantity+coverType combo
  return discountTiers
    .reduce((acc, tier) => {
      const existing = acc.find(
        (t) =>
          t.minQuantity === tier.minQuantity && t.coverType === tier.coverType
      );
      return !existing || existing.price > tier.price
        ? [
            ...acc.filter(
              (t) =>
                !(
                  t.minQuantity === tier.minQuantity &&
                  t.coverType === tier.coverType
                )
            ),
            tier
          ]
        : acc;
    }, [] as CartDiscountTier[])
    .sort((a, b) => a.minQuantity - b.minQuantity);
}

export function getTotalEquivalentQuantity(items: CartItem[]): number {
  return items.reduce(
    (total, item) =>
      total +
      (item.isKit ? item.quantity * (item.kitSize || 1) : item.quantity),
    0
  );
}

// eslint-disable-next-line max-lines-per-function
export function getActivePromotion(
  items: CartItem[],
  discountTiers: CartDiscountTier[]
): Promotion | null {
  const itemsByCoverType = items.reduce(
    (acc, item) => {
      if (!acc[item.coverType]) {
        acc[item.coverType] = [];
      }
      acc[item.coverType].push(item);
      return acc;
    },
    {} as Record<CoverEnum, CartItem[]>
  );

  let bestPromotion: Promotion | null = null;

  Object.entries(itemsByCoverType).forEach(([coverType, coverItems]) => {
    const numericCoverType = Number(coverType) as CoverEnum;
    const totalItems = getTotalEquivalentQuantity(coverItems);

    const applicableTier = [...discountTiers]
      .sort((a, b) => b.minQuantity - a.minQuantity)
      .find(
        (tier) =>
          totalItems >= tier.minQuantity && tier.coverType === numericCoverType
      );

    if (!applicableTier) {
      return;
    }

    // Caso 1: Kit + itens avulsos (ex: 1 kit de 4 + 1 unidade)
    const hasKits = items.some((item) => item.isKit);
    const hasSingleItems = items.some((item) => !item.isKit);

    if (hasKits && hasSingleItems) {
      bestPromotion = {
        title: 'O preço baixou!',
        description: `Você está levando mais livros pelo valor unitário do KIT de ${applicableTier.minQuantity} livros`,
        type: 'KIT_EXTRA'
      };
      return;
    }

    if (hasKits) {
      const kitItem = items.find((item) => item.isKit);
      const kitSize = kitItem?.kitSize || 1;
      const kitCount = Math.floor(totalItems / kitSize);
      if (kitCount > 1) {
        bestPromotion = {
          title: 'O preço mudou!',
          description: `Leve ${kitCount} KITS de ${kitSize} livros pelo valor do KIT com ${applicableTier.minQuantity} livros`,
          type: 'KIT_TIER'
        };
        return;
      }
    }

    bestPromotion = {
      title: 'O preço baixou!',
      description: `Você está levando ${totalItems} livros pelo valor unitário do KIT de ${applicableTier.minQuantity} livros`,
      type: 'UNIT_TIER'
    };
  });

  return bestPromotion;
}

export function getTotalPrice(items: CartItem[]): number {
  if (!items?.length) {
    return 0;
  }

  return parseFloat(
    items
      .reduce((total, item) => {
        const price = item.originalPrice || 0;

        return total + price * item.quantity;
      }, 0)
      .toFixed(2)
  );
}

export function getTotalDiscountedPrice(items: CartItem[]): number {
  if (!items?.length) {
    return 0;
  }

  return parseFloat(
    items
      .reduce((total, item) => {
        const price = item.price || 0;
        return total + price * item.quantity;
      }, 0)
      .toFixed(2)
  );
}
